import { MutableRefObject, useEffect, useRef } from 'react';

type Options = {
    // The rate at which the scrolling momentum decays (e.g. the closer to 1 it is, the more distance and time it takes to stop)
    scrollDecayRate?: number;
    // The minimum movement (in pixels) required to consider the drag motion intentional.
    minDragDistance?: number;
    // The mouse button that activates dragging.
    activeMouseButton?: 'Left' | 'Middle' | 'Right';
    // A flag to control whether the draggable behavior is active.
    isEnabled?: boolean;
};

type Events = {
    events: {
        onMouseDown: (e: React.MouseEvent<HTMLElement>) => void;
    };
};

export function useDragScroll(
    targetRef: MutableRefObject<HTMLElement>,
    {
        scrollDecayRate = 0.95,
        minDragDistance = 10,
        activeMouseButton = 'Left',
        isEnabled = true,
    }: Options = {}
): Events {
    const internalState = useRef({
        isMouseDown: false,
        isDragging: false,
        initialMouseX: 0,
        initialMouseY: 0,
        lastMouseX: 0,
        lastMouseY: 0,
        scrollSpeedX: 0,
        lastScrollX: 0,
    });

    let maxHorizontalScroll = 0;
    let cursorStyleOfWrapperElement: string;
    let cursorStyleOfChildElements: string[];
    let isMouseDown: boolean = false;

    const fps = (1 / 60) * 1000; // Attempt to make the effect smooth (i.e. 60fps)

    useEffect(() => {
        if (isEnabled && targetRef.current) {
            maxHorizontalScroll =
                targetRef.current.scrollWidth - targetRef.current.clientWidth;
            cursorStyleOfWrapperElement = window.getComputedStyle(
                targetRef.current
            ).cursor;

            cursorStyleOfChildElements = Array.from(
                targetRef.current.childNodes
            ).map(
                (child: HTMLElement) => window.getComputedStyle(child).cursor
            );
        }
    }, [isEnabled, targetRef]);

    const runScroll = () => {
        const dx = internalState.current.scrollSpeedX * fps;
        const offsetX = targetRef.current.scrollLeft + dx;

        targetRef.current.scrollLeft = offsetX;
        internalState.current.lastScrollX = offsetX;
    };

    const callbackMomentum = () => {
        const minSpeedToTriggerMomentum = 0.05;

        const keepMovingX = () => {
            const lastScrollSpeedX = internalState.current.scrollSpeedX;
            const newScrollSpeedX = lastScrollSpeedX * scrollDecayRate;
            internalState.current.scrollSpeedX = newScrollSpeedX;

            const isAtLeft = targetRef.current.scrollLeft <= 0;
            const isAtRight =
                targetRef.current.scrollLeft >= maxHorizontalScroll;
            const hasReachedHorizontalEdges = isAtLeft || isAtRight;

            runScroll();

            if (
                Math.abs(newScrollSpeedX) < minSpeedToTriggerMomentum ||
                isMouseDown ||
                hasReachedHorizontalEdges
            ) {
                internalState.current.scrollSpeedX = 0;
            } else {
                requestAnimationFrame(keepMovingX);
            }
        };

        internalState.current.isDragging = false;
        requestAnimationFrame(keepMovingX);
    };

    const preventClick = (e: Event) => {
        e.preventDefault();
        e.stopImmediatePropagation();
    };

    const getIsMousePressActive = (buttonsCode: number) => {
        return (
            (activeMouseButton === 'Left' && buttonsCode === 1) ||
            (activeMouseButton === 'Middle' && buttonsCode === 4) ||
            (activeMouseButton === 'Right' && buttonsCode === 2)
        );
    };

    const onMouseDown = (e: React.MouseEvent<HTMLElement>) => {
        const isMouseActive = getIsMousePressActive(e.buttons);
        if (!isMouseActive) {
            return;
        }

        isMouseDown = true;
        internalState.current.isMouseDown = true;
        internalState.current.lastMouseX = e.clientX;
        internalState.current.initialMouseX = e.clientX;
    };

    const onMouseUp = () => {
        const isDragging = internalState.current.isDragging;

        const dx =
            internalState.current.initialMouseX -
            internalState.current.lastMouseX;

        const isMotionIntentional = Math.abs(dx) > minDragDistance;

        const isDraggingConfirmed = isDragging && isMotionIntentional;

        if (isDraggingConfirmed) {
            Array.from(targetRef.current.childNodes).forEach(
                (child: HTMLElement) => {
                    child.addEventListener('click', preventClick);
                }
            );
        } else {
            Array.from(targetRef.current.childNodes).forEach(
                (child: HTMLElement) => {
                    child.removeEventListener('click', preventClick);
                }
            );
        }

        internalState.current.isMouseDown = false;
        internalState.current.lastMouseX = 0;
        isMouseDown = false;

        targetRef.current.style.cursor = cursorStyleOfWrapperElement;
        cursorStyleOfChildElements.forEach((cursor, i) => {
            (targetRef.current.childNodes[i] as HTMLElement).style.cursor =
                cursor;
        });

        if (isDraggingConfirmed) {
            callbackMomentum();
        }
    };

    const onMouseMove = (e: MouseEvent) => {
        if (!internalState.current.isMouseDown) {
            return;
        }

        e.preventDefault();

        const dx = internalState.current.lastMouseX - e.clientX;
        internalState.current.lastMouseX = e.clientX;

        internalState.current.scrollSpeedX = dx / fps;
        internalState.current.isDragging = true;

        runScroll();
    };

    const handleResize = () => {
        maxHorizontalScroll =
            targetRef.current.scrollWidth - targetRef.current.clientWidth;
    };

    useEffect(() => {
        if (isEnabled) {
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', handleResize);
        };
    }, [isEnabled]);

    return {
        events: {
            onMouseDown,
        },
    };
}
